// staffGrouping.js
// Pure grouping of staff into ordered sections for the staff views (dashboard,
// final roster, Excel). One shared helper so all three surfaces group identically.
//
// Grouping key = ANCHOR LOCATION, taken from each employee's preferred_locations
// (their anchor ward(s)). Rules agreed with ops:
//   - exactly one anchor  -> staff appears in that ward's section
//   - no anchor           -> staff appears in a single "No anchor" section
//   - more than one anchor -> staff appears (duplicated) in EACH ward's section
//
// NOTE: the institution is the HOSPITAL (NUH/NTFGH), not the ward, so it is NOT the
// grouping key. preferred_locations is the real staff->ward link.

const NO_ANCHOR_LABEL = 'No anchor';

const anchorsOf = (emp) => {
  const raw = emp?.preferred_locations || emp?.preferredLocations || [];
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
};

/**
 * Group a flat staff list into ordered [{ key, label, staff:[] }] sections.
 *
 * @param {Array} staffList  employees (already filtered/searched as desired)
 * @param {string} mode      'ward' to group by anchor location (ids; labelled
 *                           from `locationNames`); anything else
 *                           returns a single ungrouped section (caller renders
 *                           its existing flat/rank view).
 * @returns {Array<{key:string,label:string,staff:Array}>}
 */
export const groupStaff = (staffList, mode, locationOrder = [], locationNames = {}) => {
  const list = Array.isArray(staffList) ? staffList : [];
  // Ward sections follow the user-defined clinic order when one is supplied
  // (schedule.locations); wards not in it sort after, alphabetically.
  const rank = new Map((locationOrder || []).map((name, i) => [String(name), i]));
  const byOrder = (a, b) => {
    const ra = rank.has(String(a)) ? rank.get(String(a)) : rank.size;
    const rb = rank.has(String(b)) ? rank.get(String(b)) : rank.size;
    return ra - rb || String(a).localeCompare(String(b));
  };

  if (mode !== 'ward') {
    // Ungrouped: a single section the caller can render as its existing flat list.
    return [{ key: '__all__', label: '', staff: list }];
  }

  const sections = new Map();   // wardKey -> staff[]
  const noAnchor = [];

  for (const emp of list) {
    const anchors = anchorsOf(emp);
    if (anchors.length === 0) {
      noAnchor.push(emp);
      continue;
    }
    // one section per anchor; multi-anchor staff are duplicated across sections
    for (const ward of anchors) {
      if (!sections.has(ward)) sections.set(ward, []);
      sections.get(ward).push(emp);
    }
  }

  // Ward sections in the user-defined order, then the No-anchor bucket last.
  const out = [...sections.keys()]
    .sort(byOrder)
    .map((ward) => ({
      key: ward,
      label: (locationNames || {})[ward] || ward,
      staff: sections.get(ward).sort((a, b) => a.name.localeCompare(b.name)),
    }));

  if (noAnchor.length) {
    out.push({
      key: '__no_anchor__',
      label: NO_ANCHOR_LABEL,
      staff: noAnchor.sort((a, b) => a.name.localeCompare(b.name)),
    });
  }

  return out;
};
